<template>
  <v-card v-bind:class="actionClass">
    <!-- Input meta info & toolbar -->
    <v-toolbar flat v-if="level > 0" color="#f5f5ff">
      <v-toolbar-title class="blue-grey--text">
        <span class="subtitle-1">{{ $t('input') }}</span>
        <span class="caption pl-2">LVL {{ level }}</span>
        <span class="caption pl-2">IDX {{ index }}</span>
        <span class="caption pl-2">{{ typeLabel }}</span>
      </v-toolbar-title>
      <v-spacer class="handle"></v-spacer>
      <v-btn class="caption" v-bind:disabled="sourceDisabled" @click="addSource" v-if="toolbarEnabled && showAddSource">
        <v-icon>add</v-icon>
        {{ $t('source') }}
      </v-btn>
      <v-menu open-on-hover offset-y v-bind:disabled="validationDisabled" v-if="toolbarEnabled">
        <template v-slot:activator="{ on }">
          <v-btn text class="caption" v-on="on" v-bind:disabled="validationDisabled">
            <v-icon>add</v-icon>
            {{ $t('validation') }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(val, key) in validationItems"
            v-bind:key="key"
            @click="addValidation({ type: val.type })"
          >
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content class="subtitle-1">{{ val.label | friendlyLabel }}</v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn text class="caption" v-bind:disabled="fallbackDisabled" @click="addFallback" v-if="toolbarEnabled">
        <v-icon>add</v-icon>
        {{ $t('fallback') }}
      </v-btn>
      <v-menu open-on-hover bottom offset-y v-if="toolbarEnabled">
        <template v-slot:activator="{ on }">
          <v-btn text icon class="caption" v-on="on">
            <v-icon>more_vert</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-btn text icon :title="copyTitle" :color="inCopy ? 'grey' : 'black'" @click="copy">
            <v-icon>file_copy</v-icon>
          </v-btn>
          <v-btn text icon :title="cutTitle" :color="inCut ? 'grey' : 'black'" @click="cut">
            <v-icon>flip_to_back</v-icon>
          </v-btn>
          <v-btn text icon color="error" :title="$t('remove')" @click="remove">
            <v-icon>delete_forever</v-icon>
          </v-btn>
        </v-list>
      </v-menu>
    </v-toolbar>

    <!-- Input fields editor -->
    <v-container fluid class="pa-2">
      <v-layout>
        <v-flex>
          <v-text-field v-model="localLabel" :label="$t('label')"></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedLabel" :label="translatedLabel"></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-text-field v-model="localDescription" :label="$t('description')"></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedDescription" :label="translatedDescription"></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex shrink v-if="sourceWarning">
          <v-icon color="orange" class="ma-3" :title="$t('selected-source-type-not-matching-device-type')">warning
          </v-icon>
        </v-flex>
        <v-flex xs10 sm6 md4 v-if="displaySource">
          <v-autocomplete v-model="localSource"
                          :items="sourceVariables"
                          :label="$t('source')"
                          item-value="name"
                          item-text="name"
                          clearable
          ></v-autocomplete>
        </v-flex>
        <v-flex xs6 sm6 md4 v-if="displaySource && source && source.type">
          <v-select class="ml-3"
            v-model="localSourceUnit"
            :label="$t('source-type')"
            :items="localSourceUnits"
            item-value="value"
            item-text="label"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm6 md2 v-if="displaySource && localSourceUnit">
          <v-select class="ml-3"
            v-model="localSourceDisplayUnit"
            :label="$t('display-unit')"
            :items="localSourceDisplayUnits"
          ></v-select>
        </v-flex>
      </v-layout>
      <v-layout v-if="(/switch|number|string/).test(type)">
        <v-flex xs12 sm4 md2>
          <v-select
            v-model="localLength"
            :items="localLengthItems"
            :label="$t('input-style')"
          ></v-select>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Validations -->
    <v-container fluid class="px-2 pt-2 pb-0" v-if="validations.length">
      <v-card>
        <!-- Validations meta info & toolbar -->
        <v-toolbar flat>
          <v-toolbar-title class="blue-grey--text">
            <span class="subtitle-1">{{ $t('validation') }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text icon color="error" :title="$t('remove')" @click="clearValidations" v-if="toolbarEnabled">
            <v-icon>delete_forever</v-icon>
          </v-btn>
        </v-toolbar>

        <input-validation-editor
          v-for="(item, key) in validations"
          v-bind:key="key"
          v-bind:item="item"
          v-bind:parent="type"
          @remove="removeValidation"
          @update="updateValidation"
        ></input-validation-editor>
      </v-card>
    </v-container>

    <!-- Fallback -->
    <v-container fluid class="px-2 pt-2 pb-0" v-if="displayFallback">
      <v-card class="pb-2">
        <!-- Fallback meta info & toolbar -->
        <v-toolbar flat>
          <v-toolbar-title class="blue-grey--text">
            <span class="subtitle-1">Fallback</span>
            <v-icon
              v-if="fallbackWarning"
              color="orange"
              :title="$t('fallback-inaccessible-missing-validation-rules')"
            >warning
            </v-icon>
          </v-toolbar-title>

          <v-spacer></v-spacer>

          <v-menu open-on-hover offset-y v-if="toolbarEnabled">
            <template v-slot:activator="{ on }">
              <v-btn text class="caption" v-on="on">
                <v-icon>add</v-icon>
                {{ $t('info') }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="addInfo">
                <v-list-item-avatar>
                  <v-icon>add</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('text') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('block-of-text') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="addImage">
                <v-list-item-avatar>
                  <v-icon>add</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('image') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('visual-block') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu open-on-hover offset-y v-if="toolbarEnabled">
            <template v-slot:activator="{ on }">
              <v-btn text class="caption" v-on="on">
                <v-icon>add</v-icon>
                {{ $t('input') }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="addSwitchInput">
                <v-list-item-avatar>
                  <v-icon>add</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('switch') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('boolean-selector') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="addNumberInput">
                <v-list-item-avatar>
                  <v-icon>add</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('number') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('number-input') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="addStringInput">
                <v-list-item-avatar>
                  <v-icon>add</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('text') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('text-input') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="addFileInput">
                <v-list-item-avatar>
                  <v-icon>add</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('image') }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('file-input') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn text icon color="error" :title="$t('remove')" @click="removeFallback">
            <v-icon>delete_forever</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Fallback items -->
        <draggable v-model="sortedItems" handle=".handle" ghostClass="ghost" chosenClass="drag">
          <div v-for="(item, key) in items" v-bind:key="key" class="ml-2 mt-2 sortable">
            <info-editor
              v-if="(/info/).test(item.type)"
              v-bind:eventBus="eventBus"
              v-bind:type="item.type"
              v-bind:level="item.lvl"
              v-bind:index="item.idx"
              v-bind:label="item.label"
              v-bind:description="item.description"
              v-bind:translation="item.translation || {}"
            ></info-editor>
            <image-editor
              v-if="(/image/).test(item.type)"
              v-bind:eventBus="eventBus"
              v-bind:type="item.type"
              v-bind:level="item.lvl"
              v-bind:index="item.idx"
              v-bind:label="item.label"
              v-bind:description="item.description"
              v-bind:translation="item.translation || {}"
              v-bind:validations="item.validations"
              v-bind:items="item.items"
            ></image-editor>
            <input-editor
              v-if="(/switch|number|string|file/).test(item.type)"
              v-bind:eventBus="eventBus"
              v-bind:type="item.type"
              v-bind:level="item.lvl"
              v-bind:index="item.idx"
              v-bind:label="item.label"
              v-bind:description="item.description"
              v-bind:translation="item.translation || {}"
              v-bind:validations="item.validations"
              v-bind:items="item.items"
              v-bind:source="item.source || null"
            ></input-editor>
          </div>
        </draggable>
      </v-card>
    </v-container>
  </v-card>
</template>

<script>
import Draggable from 'vuedraggable'
import editorMixins from './editor-mixins.es6'
import InputValidationEditor from './editor-input-validation.vue'
import InfoEditor from './editor-info.vue'
import InputEditor from './editor-input.vue'

export default {
  name: 'input-editor',
  mixins: [editorMixins],
  props: {
    level: Number,
    index: Number,
    type: String,
    label: String,
    description: String,
    translation: Object,
    validations: Array,
    items: Array,
    source: Object,
    length: String
  },
  data () {
    return {
      fallback: false
    }
  },
  computed: {
    // source: display "add source" button
    showAddSource () {
      const hasType = this.$store.getters['checklist/deviceType'] !== null
      const hasVars = this.sourceVariables.length !== 0
      const allowed = this.type !== 'file'

      return hasType && hasVars && allowed
    },
    // source: disable "add source" if source is set
    sourceDisabled () {
      return this.source && this.source.name !== null
    },
    // source: display "source select"
    displaySource () {
      return this.showAddSource && this.sourceDisabled
    },
    sourceWarning () {
      const type = this.$store.getters['checklist/deviceType']
      return this.displaySource && this.source.type && this.source.type !== type
    },
    localSourceUnit: {
      get () {
        return this.source ? this.source.unit : null
      },
      set (value) {
        const unit = value || null
        const displayUnit = unit ? this.$store.getters['checklist/displayUnits'][unit][0] : null
        const receiveUnit = displayUnit ? this.$store.getters['checklist/receiveUnit'](unit) : null

        const source = {
          ...this.source,
          unit,
          displayUnit,
          receiveUnit
        }
        this.$nextTick(() => {
          this.eventBus.$emit('update', {
            idx: this.index,
            source
          })
        })
      }
    },
    localSourceUnits () {
      return this.$store.getters['checklist/units'].map(unit => ({
        value: unit.value,
        label: this.translateLabel(unit.label)
      }))
    },
    localSourceDisplayUnit: {
      get () {
        return this.source ? this.source.displayUnit : null
      },
      set (value) {
        const displayUnit = value || null
        const source = {
          ...this.source,
          displayUnit
        }
        this.$nextTick(() => {
          this.eventBus.$emit('update', {
            idx: this.index,
            source
          })
        })
      }
    },
    localSourceDisplayUnits () {
      const displayUnits = this.$store.getters['checklist/displayUnits']
      if (this.localSourceUnit) {
        return displayUnits[this.localSourceUnit]
      } else {
        return [null]
      }
    },
    sourceVariables () {
      const type = this.$store.getters['checklist/deviceType']
      const vars = []
      const deviceType = this.$store.state.deviceTypes.filter(({ name }) => name === type).shift()
      if (type && deviceType) {
        for (let name in deviceType.checklistVariables) {
          vars.push({
            name,
            type
          })
        }
      }
      return vars
    },
    typeLabel () {
      let type = this.type
      if (/string/.test(type)) {
        type = this.$t('text')
      }
      if (/file/.test(type)) {
        type = this.$t('image')
      }
      return type.toUpperCase()
    },
    validationItems () {
      const items = [
        { type: 'equals', label: this.$t('equals') },
        { type: 'contains', label: this.$t('contains') },
        { type: 'isLessThan', label: this.$t('is-less-than') },
        { type: 'isLessOrEqualThan', label: this.$t('is-less-or-equal-than') },
        { type: 'isMoreThan', label: this.$t('is-more-than') },
        { type: 'isMoreOrEqualThan', label: this.$t('is-more-or-equal-than') }
      ]
      let availableItems = []

      switch (this.type) {
        case 'switch':
          availableItems = items.reduce((items, item, i) => {
            if (i === 0) items.push(item) // just first
            return items
          }, [])
          break
        case 'string':
          availableItems = items.reduce((items, item, i) => {
            if (i === 0 || i === 1) items.push(item) // first two
            return items
          }, [])
          break
        case 'number':
        case 'file':
          availableItems = items.reduce((items, item, i) => {
            if (i !== 1) { // all but second
              items.push(item)
            }
            return items
          }, [])
          break
      }

      // filter out assigned items
      return availableItems.filter(available => {
        return !this.validations.some(item => item.type === available.type)
      })
    },
    validationDisabled () {
      // adding validations is disabled when whole available set is used (assigned)
      return this.validationItems.length === 0
    },
    displayFallback () {
      return this.items.length > 0 || this.fallback
    },
    fallbackWarning () {
      return this.displayFallback && this.validations.length === 0
    },
    fallbackDisabled () {
      // fallback is disbaled when no validation is added (yet) or fallbeck items are added (already)
      return this.validations.length === 0 || this.items.length > 0
    },
    localLengthItems () {
      return [
        { value: 'wide', text: this.$t('wide') },
        { value: 'narrow', text: this.$t('narrow') }
      ]
    },
    localLength: {
      get () {
        return this.length || null
      },
      set (length) {
        this.$nextTick(() => {
          this.eventBus.$emit('update', {
            idx: this.index,
            length
          })
        })
      }
    }
  },
  methods: {
    addSource () {
      this.eventBus.$emit('update', {
        idx: this.index,
        source: {
          name: '',
          type: '',
          unit: null,
          displayUnit: null
        }
      })
    },
    addValidation ({ type }) {
      const defaultValue = (/switch/).test(this.type) && (/equals/).test(type)
        ? true
        : null
      const validations = this.validations.concat({
        type,
        value: defaultValue
      })
      this.eventBus.$emit('update', {
        idx: this.index,
        validations
      })
    },
    removeValidation ({ type }) {
      const validations = this.validations.reduce((validations, item) => {
        if (item.type !== type) {
          validations.push(item)
        }
        return validations
      }, [])

      this.eventBus.$emit('update', {
        idx: this.index,
        validations
      })
    },
    updateValidation ({ type, value }) {
      const validations = this.validations.reduce((validations, item) => {
        if (item.type === type) {
          item.value = value
        }
        validations.push(item)
        return validations
      }, [])

      this.eventBus.$emit('update', {
        idx: this.index,
        validations
      })
    },
    clearValidations () {
      this.eventBus.$emit('update', {
        idx: this.index,
        validations: []
      })
    },
    addFallback () {
      this.fallback = true
    },
    removeFallback () {
      // clear items
      this.eventBus.$emit('update', {
        idx: this.index,
        items: []
      })
      this.fallback = false
    },
    // Append new item: info
    addInfo () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInfo()
      })
    },
    addImage () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createImage()
      })
    },

    addSwitchInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('switch')
      })
    },

    addNumberInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('number')
      })
    },

    addStringInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('string')
      })
    },

    addFileInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('file')
      })
    },

    createInfo () {
      return {
        idx: this.nextIdx,
        lvl: this.nextLevel,
        type: 'info',
        label: '',
        description: ''
      }
    },

    createImage () {
      return {
        idx: this.nextIdx,
        lvl: this.nextLevel,
        type: 'image',
        items: [],
        label: '',
        description: ''
      }
    },

    createInput (type) {
      return {
        idx: this.nextIdx,
        lvl: this.nextLevel,
        type,
        label: '',
        description: '',
        validations: [],
        items: [],
        source: null,
        style: 'wide'
      }
    },

    translateLabel (label) {
      const prefix = 'units'
      const result = this.$t([prefix, label.toLowerCase()].join('-'))
      return result.indexOf(prefix) === 0
        ? label
        : result
    }
  },
  filters: {
    friendlyLabel (val) {
      return val.replace(/([A-Z])/g, ' $1').toLowerCase()
    }
  },
  components: {
    Draggable,
    InputValidationEditor,
    InfoEditor,
    InputEditor
  }
}
</script>
